import { useState, useEffect } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';

export const useLearnAuth = () => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [userRoles, setUserRoles] = useState<string[]>([]);
  const [isAdmin, setIsAdmin] = useState(false);
  const [rolesLoaded, setRolesLoaded] = useState(false);

  // Separate function to fetch user roles
  const fetchUserRoles = async (userId: string) => {
    try {
      const { data: roles } = await supabase
        .from('user_roles')
        .select('role')
        .eq('user_id', userId);
      
      const roleList = roles?.map(r => r.role) || [];
      setUserRoles(roleList);
      setIsAdmin(
        roleList.includes('provider_admin') || 
        roleList.includes('chamber_admin') || 
        roleList.includes('trainer')
      );
      setRolesLoaded(true);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching user roles:', error);
      setUserRoles([]);
      setIsAdmin(false);
      setRolesLoaded(true);
      setLoading(false);
    }
  };

  useEffect(() => {
    // Set up auth state listener first
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        setSession(session);
        setUser(session?.user ?? null);
        
        // Fetch user roles in a deferred manner to avoid timing issues
        if (session?.user?.id) {
          fetchUserRoles(session.user.id);
        } else {
          setUserRoles([]);
          setIsAdmin(false);
          setRolesLoaded(true);
          setLoading(false);
        }
      }
    );

    // Then check for existing session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      
      // Fetch user roles for existing session
      if (session?.user?.id) {
        fetchUserRoles(session.user.id);
      } else {
        setLoading(false);
        setRolesLoaded(true);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const signOut = async () => {
    await supabase.auth.signOut();
  };

  return {
    user,
    session,
    loading,
    signOut,
    isAuthenticated: !!user,
    userRoles,
    isAdmin,
    rolesLoaded,
    hasRole: (role: string) => userRoles.includes(role),
  };
};
